/**
 * Query keys for React Query
 * Centralized query key constants to ensure consistency and prevent typos
 *
 * ⚠️ MANDATORY: Always use QUERY_KEYS for all queries to prevent cache invalidation bugs
 *
 * @example
 * // ✅ CORRECT - Use factory
 * const { data } = useQuery({
 *   queryKey: QUERY_KEYS.PROFILE(userId),
 *   queryFn: () => fetchProfile(userId)
 * });
 *
 * // ❌ WRONG - Hard-coded key
 * const { data } = useQuery({
 *   queryKey: ['profile', userId], // Don't do this!
 *   queryFn: () => fetchProfile(userId)
 * });
 *
 * // Invalidation
 * queryClient.invalidateQueries({
 *   queryKey: QUERY_KEYS.PROFILE(userId)
 * });
 */
export const QUERY_KEYS = {
  // Profile queries
  PROFILE: (userId: string) => ['profile', userId] as const,
  PROFILE_DATA: (userId: string) => ['profile-data', userId] as const,
  HEALTH_CONSENT: (userId: string, version?: number) =>
    version === undefined ? (['health-consent', userId] as const) : (['health-consent', userId, version] as const),
  MEMBER_ADMISSION: (userId: string, version?: number) =>
    version === undefined ? (['member-admission', userId] as const) : (['member-admission', userId, version] as const),
  FIRST_RUN: (userId: string, version?: number) =>
    version === undefined ? (['first-run', userId] as const) : (['first-run', userId, version] as const),
  USER_COMPANY: (userId: string) => ['user-company', userId] as const,

  // Dashboard queries
  DASHBOARD: (userId: string, date?: string) =>
    date ? (['dashboard', userId, date] as const) : (['dashboard', userId] as const),
  DASHBOARD_METRICS: (userId: string) => ['dashboard-metrics', userId] as const,
  DAILY_RECOMMENDATION: (userId: string, date?: string, locale?: string) =>
    date && locale
      ? (['daily-recommendation', userId, date, locale] as const)
      : (['daily-recommendation', userId] as const),
  METRICS: (userId: string, date: string) => ['metrics', userId, date] as const,
  POINTS: (userId: string) => ['points', userId] as const,
  STREAK: (userId: string) => ['streak', userId] as const,
  LATEST_CHECKIN: (userId: string) => ['latestCheckin', userId] as const,
  LATEST_RECOVERY_INPUT: (userId: string) => ['latestRecoveryInput', userId] as const,
  TODAY_RECOVERY_INPUT: (userId: string, timezone: string) =>
    ['todayRecoveryInput', userId, timezone] as const,
  HEALTH_SCORE: (userId: string) => ['health-score', userId] as const,
  HEALTH_SCORE_CALIBRATION: (userId: string) => ['health-score-calibration', userId] as const,
  HEALTH_SUMMARY_CHAT: (userId: string) => ['health-summary-chat', userId] as const,
  HEALTH_EVENTS: (userId: string) => ['health-events', userId] as const,
  NUTRITION_DAY_SUMMARY: (userId: string, date: string) =>
    ['nutrition-day-summary', userId, date] as const,
  NUTRITION_RANGE_SUMMARY: (userId: string, startDate: string, endDate: string) =>
    ['nutrition-range-summary', userId, startDate, endDate] as const,
  NUTRITION_ENTRIES: (userId: string, date?: string, limit?: number) =>
    date !== undefined || limit !== undefined
      ? (['nutrition-entries', userId, date ?? 'all', limit ?? 'default'] as const)
      : (['nutrition-entries', userId] as const),
  NUTRITION_CONTEXT: (userId: string, windowDays = 7) =>
    ['nutrition-context', userId, windowDays] as const,
  NUTRITION_PROTEIN_REFERENCE: (userId: string) => ['nutrition-protein-reference', userId] as const,
  NUTRITION_CALORIE_TARGET: (userId: string) => ['nutrition-calorie-target', userId] as const,
  HYDRATION_DAY_SUMMARY: (userId: string, date: string) =>
    ['hydration-day-summary', userId, date] as const,
  HYDRATION_ENTRIES: (userId: string, date: string) => ['hydration-entries', userId, date] as const,
  DIARY_MEMORIES: (userId: string, limit?: number) =>
    limit !== undefined
      ? (['diary-memories', userId, limit] as const)
      : (['diary-memories', userId] as const),
  DIARY_CONTEXT: (userId: string, limit = 12) => ['diary-context', userId, limit] as const,
  VAULT_DOCUMENTS: (userId: string) => ['vault-documents', userId] as const,
  VAULT_COUNT: (userId: string) => ['vault-count', userId] as const,
  TOTAL_POINTS: (userId: string) => ['total-points', userId] as const,
  // Historical data queries
  HISTORICAL_METRICS: (userId: string, range: string) =>
    ['historical-metrics', userId, range] as const,
  HISTORICAL_METRICS_INFINITE: (userId: string, range?: string) =>
    range
      ? (['historical-metrics-infinite', userId, range] as const)
      : (['historical-metrics-infinite', userId] as const),
  POINTS_HISTORY_INFINITE: (userId: string) => ['points-history-infinite', userId] as const,
  HISTORICAL_POINTS: (userId: string, range: string) =>
    ['historical-points', userId, range] as const,

  // Leaderboard queries
  LEADERBOARD: (userId: string) => ['leaderboard', userId] as const,
  LEADERBOARD_ALL: () => ['leaderboard'] as const,
  LEADERBOARD_DATA: (userId: string) => ['leaderboard-data', userId] as const,
  USER_RANK: (userId: string) => ['user-rank', userId] as const,
  FRIENDS_LEADERBOARD: (userId: string) => ['friends-leaderboard', userId] as const,
  FRIENDS_LEADERBOARD_ALL: () => ['friends-leaderboard'] as const,

  // Rewards queries
  REWARDS: () => ['rewards'] as const,
  REWARDS_DATA: (userId: string) => ['rewards-data', userId] as const,
  REWARD_STOCK: () => ['reward-stock'] as const,
  REDEMPTIONS: (userId: string) => ['reward-redemptions', userId] as const,
  OWNED_COUPONS: (userId: string) => ['owned-coupons', userId] as const,

  // Fitbit queries
  FITBIT_STATUS: () => ['fitbit-status'] as const,
  FITBIT_STATUS_USER: (userId: string) => ['fitbit-status', userId] as const,
  FITBIT_LAST_SYNC: (userId: string) => ['fitbit-last-sync', userId] as const,
  FITBIT_LAST_SYNC_PROVIDER: (userId: string, provider: string, wearableProvider: string) =>
    ['fitbit-last-sync', userId, provider, wearableProvider] as const,
  FITBIT_DATA: (userId: string) => ['fitbit-data', userId] as const,
  FITBIT_SYNC_STATUS: (userId: string) => ['fitbit-sync-status', userId] as const,
  FITBIT_SYNC_HISTORY: (userId: string) => ['fitbit-sync-history', userId] as const,
  FITBIT_RAW_SLEEP: (userId: string) => ['fitbit-raw-sleep', userId] as const,
  FITBIT_WEBHOOK_STATUS: () => ['fitbit-webhook-status'] as const,

  // Wearable queries (Open Wearables multi-provider)
  WEARABLE_CONNECTIONS: (userId: string) => ['wearable-connections', userId] as const,
  WEARABLE_PROVIDERS: () => ['wearable-providers'] as const,

  // User features queries
  ANAMNESEBOGEN: (userId: string) => ['anamnesebogen', userId] as const,
  MFA_FACTORS: (userId: string) => ['mfa-factors', userId] as const,
  MFA_GATE: (userId: string, version?: number) =>
    version === undefined ? (['mfa-gate', userId] as const) : (['mfa-gate', userId, version] as const),

  // Training queries
  TRAINING_PLANS: (userId: string) => ['training-plans', userId] as const,
  ACTIVE_TRAINING_TEMPLATE: (userId: string) => ['active-training-template', userId] as const,
  ACTIVE_TRAINING_TEMPLATE_DETAIL: (userId: string) =>
    ['active-training-template-detail', userId] as const,
  ACTIVE_ONE_OFF_TRAINING_WORKOUT: (userId: string) =>
    ['active-one-off-training-workout', userId] as const,
  TRAINING_QUEUE: (userId: string, horizonDays: number) =>
    ['training-queue', userId, horizonDays] as const,
  EXERCISES: (searchTerm: string, limit: number) => ['exercises', searchTerm, limit] as const,
  USER_EXERCISES: (userId: string) => ['user-exercises', userId] as const,
  EXERCISE_INSIGHTS: (userId: string, exerciseKey: string) =>
    ['exercise-insights', userId, exerciseKey] as const,
  COACH_WEEKLY_BLOG_INSIGHT: (userId: string, weekStartIso: string, lang: string) =>
    ['coach-weekly-blog-insight', userId, weekStartIso, lang] as const,

  // Admin queries
  ADMIN_USERS: () => ['admin-users'] as const,
  ADMIN_AUDIT_LOGS: () => ['admin-audit-logs'] as const,
  COMPANY_ADMIN_SEAT_USAGE: (userId: string) => ['company-admin-seat-usage', userId] as const,
  COMPANY_WELLNESS_REPORTS: (userId: string) => ['company-wellness-reports', userId] as const,
  COMPANY_WELLNESS_REPORT: (userId: string, reportId: string) =>
    ['company-wellness-report', userId, reportId] as const,

  // RBAC queries
  RBAC_USER_ROLES: (userId: string) => ['rbac-user-roles', userId] as const,
  USER_ROLES: (userId: string) => ['user-roles', userId] as const,

  // Health Actions (persistent nudge checklist)
  HEALTH_ACTIONS: (userId: string) => ['health-actions', userId] as const,
  PUSH_NOTIFICATION_SETTINGS: (userId: string) => ['push-notification-settings', userId] as const,
  RECOVERY_REMINDER_SCHEDULES: (userId: string) => ['recovery-reminder-schedules', userId] as const,

  // Batched data queries
  DASHBOARD_DATA_BATCHED: (userId: string) => ['dashboard-data-batched', userId] as const,
} as const;

// Type helper for query key inference
export type QueryKeys = typeof QUERY_KEYS;
